import { Controller } from 'react-hook-form';
import {
  Autocomplete,
  DateInput,
  DateInputSize,
  InputSize,
  InputText,
  Select,
  SelectSize,
  Textarea,
} from '@ui/src';
import AdditionalInfoBlock from '../../../individual/aml/additional-info-block';
import { PepTypes } from './types';
import { CustomerStatus } from '../../../../hoc/customers/types';
import RegulatedCompanyRow from './row';

import cn from './styles.module.css';

const Pep = ({
  control,
  clearErrors,
  setValue,
  status,
  errors,
  fields,
  relationshipToTheCustomerDictionary,
  countries,
  isIndividual,
}: PepTypes) => (
  <AdditionalInfoBlock title="PEP’s Questionnaire">
    <>
      <RegulatedCompanyRow
        title="Were or are you or an immediate close relative or a close associate, a
          PEP?"
      >
        <Controller
          control={control}
          name="isPepOrAssociate"
          defaultValue=""
          render={({ field: { onChange, value, name } }) => (
            <Select
              name={name}
              placeholder="Select an option..."
              list={[
                {
                  name: 'Yes',
                  value: 'yes',
                },
                {
                  name: 'No',
                  value: 'no',
                },
              ]}
              value={value as string}
              size={SelectSize.SMALL}
              onChange={(e) => {
                onChange(e);
                clearErrors(['isPepOrAssociate']);

                if (e.target.value === 'no') {
                  clearErrors([
                    'nameOfPep',
                    'relToPep',
                    'orgEmployerAndPosition',
                    'positionHeldCountryId',
                    'politicalFunctionPeriodFrom',
                    'politicalFunctionPeriodTo',
                    'abilityMoveGovernmentFunds',
                    'abilityMoveGovernmentFundsDetails',
                  ]);

                  if (!isIndividual) {
                    clearErrors(['relToCustomerId']);
                  }

                  setValue('nameOfPep', '');
                  setValue('relToPep', '');
                  setValue('orgEmployerAndPosition', '');
                  setValue('positionHeldCountryId', '');
                  setValue('politicalFunctionPeriodFrom', '');
                  setValue('politicalFunctionPeriodTo', '');
                  setValue('abilityMoveGovernmentFunds', '');
                  setValue('abilityMoveGovernmentFundsDetails', '');

                  if (!isIndividual) {
                    setValue('relToCustomerId', '');
                  }
                }
              }}
              selectWrapperClassName={
                status === CustomerStatus.CLOSED ? cn.selectDisabled : ''
              }
              disabled={status === CustomerStatus.CLOSED}
              tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
              error={errors?.isPepOrAssociate?.message}
              fullWidth
              borderless
            />
          )}
        />
      </RegulatedCompanyRow>

      {fields?.isPepOrAssociate === 'yes' && (
        <>
          <RegulatedCompanyRow title="Name of the PEP">
            <Controller
              control={control}
              name="nameOfPep"
              defaultValue=""
              render={({ field: { onChange, value, name } }) => (
                <InputText
                  name={name}
                  placeholder="Enter name..."
                  value={value}
                  size={InputSize.SMALL}
                  onChange={(e) => {
                    onChange(e);
                    clearErrors(['nameOfPep']);
                  }}
                  className="borderless"
                  disabled={status === CustomerStatus.CLOSED}
                  tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
                  error={errors?.nameOfPep?.message}
                />
              )}
            />
          </RegulatedCompanyRow>
          {!isIndividual && (
            <RegulatedCompanyRow title="Relationship to the Customer">
              <Controller
                control={control}
                name="relToCustomerId"
                defaultValue=""
                render={({ field: { onChange, value, name } }) => (
                  <Select
                    name={name}
                    placeholder="Select an option..."
                    list={
                      relationshipToTheCustomerDictionary?.map((item) => ({
                        name: item.value,
                        value: item.id,
                      })) || []
                    }
                    value={value}
                    size={SelectSize.SMALL}
                    onChange={(e) => {
                      onChange(e);
                      clearErrors(['relToCustomerId']);
                    }}
                    selectWrapperClassName={
                      status === CustomerStatus.CLOSED ? cn.selectDisabled : ''
                    }
                    disabled={status === CustomerStatus.CLOSED}
                    tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
                    error={errors?.relToCustomerId?.message}
                    fullWidth
                    borderless
                  />
                )}
              />
            </RegulatedCompanyRow>
          )}

          <RegulatedCompanyRow title="Relationship to PEP (optional)">
            <Controller
              control={control}
              name="relToPep"
              defaultValue=""
              render={({ field: { onChange, value, name } }) => (
                <Textarea
                  name={name}
                  title=""
                  placeholder="Enter description..."
                  value={value}
                  onChange={(e) => {
                    onChange(e);
                    clearErrors(['relToPep']);
                  }}
                  className={`borderless ${cn.pepTextarea} ${cn.stepOneTextarea}`}
                  error={errors?.relToPep?.message}
                  disabled={status === CustomerStatus.CLOSED}
                  tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
                />
              )}
            />
          </RegulatedCompanyRow>
          <RegulatedCompanyRow title="Organization/employer and position">
            <Controller
              control={control}
              name="orgEmployerAndPosition"
              defaultValue=""
              render={({ field: { onChange, value, name } }) => (
                <InputText
                  name={name}
                  placeholder="Enter name..."
                  value={value}
                  size={InputSize.SMALL}
                  onChange={(e) => {
                    onChange(e);
                    clearErrors(['orgEmployerAndPosition']);
                  }}
                  className="borderless"
                  disabled={status === CustomerStatus.CLOSED}
                  tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
                  error={errors?.orgEmployerAndPosition?.message}
                />
              )}
            />
          </RegulatedCompanyRow>
          <RegulatedCompanyRow title="Country of the position held">
            <Controller
              control={control}
              name="positionHeldCountryId"
              defaultValue=""
              render={({ field: { onChange, value, name } }) => (
                <Autocomplete
                  name={name}
                  placeholder="Select country..."
                  list={countries}
                  value={value}
                  size={SelectSize.SMALL}
                  onChange={(e) => {
                    onChange(e);
                    clearErrors(['positionHeldCountryId']);
                  }}
                  selectWrapperClassName={
                    status === CustomerStatus.CLOSED ? cn.selectDisabled : ''
                  }
                  disabled={status === CustomerStatus.CLOSED}
                  tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
                  error={errors?.positionHeldCountryId?.message}
                  fullWidth
                  borderless
                />
              )}
            />
          </RegulatedCompanyRow>
          <RegulatedCompanyRow title="Period in which this individual has held the political or public function">
            <div className={cn.pepPeriodRow}>
              <div className={cn.pepPeriodItem}>
                <Controller
                  control={control}
                  name="politicalFunctionPeriodFrom"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <DateInput
                      placeholder="From"
                      value={value}
                      onChange={(e) => {
                        onChange(e);
                        clearErrors(['politicalFunctionPeriodFrom']);
                      }}
                      size={DateInputSize.SMALL}
                      error={errors?.politicalFunctionPeriodFrom?.message}
                      disabled={status === CustomerStatus.CLOSED}
                      tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
                      withoutCustomSelector
                      borderless
                    />
                  )}
                />
              </div>
              <div className={cn.pepPeriodItem}>
                <Controller
                  control={control}
                  name="politicalFunctionPeriodTo"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <DateInput
                      placeholder="To"
                      value={value}
                      onChange={(e) => {
                        onChange(e);
                        clearErrors(['politicalFunctionPeriodTo']);
                      }}
                      size={DateInputSize.SMALL}
                      error={errors?.politicalFunctionPeriodTo?.message}
                      disabled={status === CustomerStatus.CLOSED}
                      tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
                      withoutCustomSelector
                      borderless
                    />
                  )}
                />
              </div>
            </div>
          </RegulatedCompanyRow>
          <RegulatedCompanyRow title="Ability to move government funds during the period">
            <Controller
              control={control}
              name="abilityMoveGovernmentFunds"
              defaultValue=""
              render={({ field: { onChange, value, name } }) => (
                <Select
                  name={name}
                  placeholder="Select an option..."
                  list={[
                    {
                      name: 'Yes',
                      value: 'yes',
                    },
                    {
                      name: 'No',
                      value: 'no',
                    },
                  ]}
                  value={value as string}
                  size={SelectSize.SMALL}
                  onChange={(e) => {
                    onChange(e);
                    clearErrors(['abilityMoveGovernmentFunds']);
                  }}
                  selectWrapperClassName={
                    status === CustomerStatus.CLOSED ? cn.selectDisabled : ''
                  }
                  disabled={status === CustomerStatus.CLOSED}
                  tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
                  error={errors?.abilityMoveGovernmentFunds?.message}
                  fullWidth
                  borderless
                />
              )}
            />
          </RegulatedCompanyRow>
          {fields?.abilityMoveGovernmentFunds === 'yes' && (
            <RegulatedCompanyRow title="Provide details">
              <Controller
                control={control}
                name="abilityMoveGovernmentFundsDetails"
                defaultValue=""
                render={({ field: { onChange, value, name } }) => (
                  <Textarea
                    name={name}
                    title=""
                    placeholder="Enter details..."
                    value={value}
                    onChange={(e) => {
                      onChange(e);
                      clearErrors(['abilityMoveGovernmentFundsDetails']);
                    }}
                    className={`borderless ${cn.pepTextarea} ${cn.stepOneTextarea}`}
                    error={errors?.abilityMoveGovernmentFundsDetails?.message}
                    disabled={status === CustomerStatus.CLOSED}
                    tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
                  />
                )}
              />
            </RegulatedCompanyRow>
          )}
        </>
      )}
    </>
  </AdditionalInfoBlock>
);

export default Pep;
